<template>
  <MainLayout>
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute -left-[1vw] top-[-12vh] w-[20vw] opacity-80"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <section id="hero" class="max-w-[1080px] lg:m-[auto] px-6">
      <div class="flex items-center lg:mt-[4rem] lg:mb-[6rem]">
        <div class="flex flex-col gap-y-4.5 lg:gap-y-12 w-full">
          <h1
            class="text-[32px] leading-[36px] lg:text-[70px] lg:leading-[74px] font-bold text-dark text-center font-markPro lg:max-w-[20ch] tracking-[-0.1rem] m-auto">
            {{ t('pages.info.terms.title') }}
          </h1>

          <div class="flex flex-col text-center leading-[27px] lg:text-lg font-semibold">
            <p class="text-lighter">
              {{ t('pages.info.terms.company_info') }}
            </p>
            <p class="text-dark">
              {{ t('pages.info.terms.last_updated') }}
            </p>
          </div>

          <div class="flex flex-col lg:flex-row gap-12 items-start mt-8">
            <aside
              class="hidden lg:block lg:w-1/4 sticky top-24 max-h-[84vh] overflow-y-auto custom-scrollbar">
              <nav class="flex flex-col gap-y-1.5 p-1">
                <p class="text-[11px] font-bold uppercase tracking-[0.15em] text-lighter mb-4 px-4">
                  {{ t('pages.info.terms.table_of_contents') }}
                </p>

                <a
                  v-for="i in 21"
                  :key="i"
                  :href="'#artikel-' + i"
                  class="group flex flex-col text-[14px] font-semibold text-lighter hover:text-primary hover:bg-white hover:shadow-sm border border-transparent hover:border-[#ECEDF0] transition-all duration-200 py-2.5 px-4 rounded-xl">
                  <span>{{ t('pages.info.terms.articles.label') }} {{ i }}</span>
                  <span class="text-[12px] font-medium opacity-60 group-hover:opacity-100">
                    {{ t(`pages.info.terms.articles.art${i}.title`) }}
                  </span>
                </a>
              </nav>
            </aside>

            <div
              class="w-full lg:w-3/4 bg-white rounded-3xl p-8 lg:p-16 shadow-sm border border-[#ECEDF0]">
              <div class="prose prose-slate max-w-none">
                <section id="artikel-1" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >1</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 1 –
                    {{ t('pages.info.terms.articles.art1.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong class="text-dark"
                        >{{ t('pages.info.terms.articles.art1.terms') }}:</strong
                      >
                      {{ t('pages.info.terms.articles.art1.terms_desc') }}
                    </p>
                    <p>
                      <strong class="text-dark"
                        >{{ t('pages.info.terms.articles.art1.bistroo') }}:</strong
                      >
                      {{ t('pages.info.terms.articles.art1.bistroo_desc') }}
                    </p>
                    <p>
                      <strong class="text-dark"
                        >{{ t('pages.info.terms.articles.art1.agreement') }}:</strong
                      >
                      {{ t('pages.info.terms.articles.art1.agreement_desc') }}
                    </p>
                    <p>
                      <strong class="text-dark"
                        >{{ t('pages.info.terms.articles.art1.restaurant') }}:</strong
                      >
                      {{ t('pages.info.terms.articles.art1.restaurant_desc') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-2" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >2</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 2 –
                    {{ t('pages.info.terms.articles.art2.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>2.1</strong>
                      {{ t('pages.info.terms.articles.art2.p1') }}
                    </p>
                    <p>
                      <strong>2.2</strong>
                      {{ t('pages.info.terms.articles.art2.p2') }}
                    </p>
                    <p>
                      <strong>2.3</strong>
                      {{ t('pages.info.terms.articles.art2.p3') }}
                    </p>
                    <p>
                      <strong>2.4</strong>
                      {{ t('pages.info.terms.articles.art2.p4') }}
                    </p>
                    <p>
                      <strong>2.5</strong>
                      {{ t('pages.info.terms.articles.art2.p5') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-3" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >3</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 3 –
                    {{ t('pages.info.terms.articles.art3.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>3.1</strong>
                      {{ t('pages.info.terms.articles.art3.p1') }}
                    </p>
                    <p>
                      <strong>3.2</strong>
                      {{ t('pages.info.terms.articles.art3.p2') }}
                    </p>
                    <p>
                      <strong>3.3</strong>
                      {{ t('pages.info.terms.articles.art3.p3') }}
                    </p>
                    <p>
                      <strong>3.4</strong>
                      {{ t('pages.info.terms.articles.art3.p4') }}
                    </p>
                    <p>
                      <strong>3.5</strong>
                      {{ t('pages.info.terms.articles.art3.p5') }}
                    </p>
                    <p>
                      <strong>3.6</strong>
                      {{ t('pages.info.terms.articles.art3.p6') }}
                    </p>
                    <p>
                      <strong>3.7</strong>
                      {{ t('pages.info.terms.articles.art3.p7') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-4" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >4</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 4 –
                    {{ t('pages.info.terms.articles.art4.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>4.1</strong>
                      {{ t('pages.info.terms.articles.art4.p1') }}
                    </p>
                    <p>
                      <strong>4.2</strong>
                      {{ t('pages.info.terms.articles.art4.p2') }}
                    </p>
                    <p>
                      <strong>4.3</strong>
                      {{ t('pages.info.terms.articles.art4.p3') }}
                    </p>
                    <p>
                      <strong>4.4</strong>
                      {{ t('pages.info.terms.articles.art4.p4') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-5" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >5</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 5 –
                    {{ t('pages.info.terms.articles.art5.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>5.1</strong>
                      {{ t('pages.info.terms.articles.art5.p1') }}
                    </p>
                    <p>
                      <strong>5.2</strong>
                      {{ t('pages.info.terms.articles.art5.p2') }}
                    </p>
                    <p>
                      <strong>5.3</strong>
                      {{ t('pages.info.terms.articles.art5.p3') }}
                    </p>
                    <p>
                      <strong>5.4</strong>
                      {{ t('pages.info.terms.articles.art5.p4') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-6" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >6</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 6 –
                    {{ t('pages.info.terms.articles.art6.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>6.1</strong>
                      {{ t('pages.info.terms.articles.art6.p1') }}
                    </p>
                    <p>
                      <strong>6.2</strong>
                      {{ t('pages.info.terms.articles.art6.p2') }}
                    </p>
                    <p>
                      <strong>6.3</strong>
                      {{ t('pages.info.terms.articles.art6.p3') }}
                    </p>
                    <p>
                      <strong>6.4</strong>
                      {{ t('pages.info.terms.articles.art6.p4') }}
                    </p>
                    <p>
                      <strong>6.5</strong>
                      {{ t('pages.info.terms.articles.art6.p5') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-7" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >7</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 7 –
                    {{ t('pages.info.terms.articles.art7.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>7.1</strong>
                      {{ t('pages.info.terms.articles.art7.p1') }}
                    </p>
                    <p>
                      <strong>7.2</strong>
                      {{ t('pages.info.terms.articles.art7.p2') }}
                    </p>
                    <p>
                      <strong>7.3</strong>
                      {{ t('pages.info.terms.articles.art7.p3') }}
                    </p>
                    <p>
                      <strong>7.4</strong>
                      {{ t('pages.info.terms.articles.art7.p4') }}
                    </p>
                    <p>
                      <strong>7.5</strong>
                      {{ t('pages.info.terms.articles.art7.p5') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-8" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >8</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 8 –
                    {{ t('pages.info.terms.articles.art8.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>8.1</strong>
                      {{ t('pages.info.terms.articles.art8.p1') }}
                    </p>
                    <p>
                      <strong>8.2</strong>
                      {{ t('pages.info.terms.articles.art8.p2') }}
                    </p>
                    <p>
                      <strong>8.3</strong>
                      {{ t('pages.info.terms.articles.art8.p4') }}
                    </p>
                    <p>
                      <strong>8.4</strong>
                      {{ t('pages.info.terms.articles.art8.p7') }}
                    </p>
                    <p>
                      <strong>8.5</strong>
                      {{ t('pages.info.terms.articles.art8.p8') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-9" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >9</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 9 –
                    {{ t('pages.info.terms.articles.art9.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>9.1</strong>
                      {{ t('pages.info.terms.articles.art9.p1') }}
                    </p>
                    <p>
                      <strong>9.2</strong>
                      {{ t('pages.info.terms.articles.art9.p2') }}
                    </p>
                    <p>
                      <strong>9.3</strong>
                      {{ t('pages.info.terms.articles.art9.p3') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-10" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >10</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 10 –
                    {{ t('pages.info.terms.articles.art10.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>10.1</strong>
                      {{ t('pages.info.terms.articles.art10.p1') }}
                    </p>
                    <p>
                      <strong>10.2</strong>
                      {{ t('pages.info.terms.articles.art10.p2') }}
                    </p>
                    <p>
                      <strong>10.3</strong>
                      {{ t('pages.info.terms.articles.art10.p3') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-11" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >11</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 11 –
                    {{ t('pages.info.terms.articles.art11.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>11.1</strong>
                      {{ t('pages.info.terms.articles.art11.p1') }}
                    </p>
                    <p>
                      <strong>11.2</strong>
                      {{ t('pages.info.terms.articles.art11.p3') }}
                    </p>
                    <p>
                      <strong>11.3</strong>
                      {{ t('pages.info.terms.articles.art11.p6') }}
                    </p>
                    <p>
                      <strong>11.4</strong>
                      {{ t('pages.info.terms.articles.art11.p8') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-12" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >12</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 12 –
                    {{ t('pages.info.terms.articles.art12.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>12.1</strong>
                      {{ t('pages.info.terms.articles.art12.p1') }}
                    </p>
                    <p>
                      <strong>12.2</strong>
                      {{ t('pages.info.terms.articles.art12.p2') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-13" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >13</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 13 –
                    {{ t('pages.info.terms.articles.art13.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>13.1</strong>
                      {{ t('pages.info.terms.articles.art13.p1') }}
                    </p>
                    <p>
                      <strong>13.2</strong>
                      {{ t('pages.info.terms.articles.art13.p2') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-14" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >14</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 14 –
                    {{ t('pages.info.terms.articles.art14.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>14.1</strong>
                      {{ t('pages.info.terms.articles.art14.p1') }}
                    </p>
                    <p>
                      <strong>14.2</strong>
                      {{ t('pages.info.terms.articles.art14.p2') }}
                    </p>
                    <p>
                      <strong>14.3</strong>
                      {{ t('pages.info.terms.articles.art14.p4') }}
                    </p>
                    <p>
                      <strong>14.5</strong>
                      {{ t('pages.info.terms.articles.art14.p6') }}
                    </p>
                    <p>
                      <strong>14.6</strong>
                      {{ t('pages.info.terms.articles.art14.p12') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-15" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >15</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 15 –
                    {{ t('pages.info.terms.articles.art15.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>15.1</strong>
                      {{ t('pages.info.terms.articles.art15.p1') }}
                    </p>
                    <p>
                      <strong>15.2</strong>
                      {{ t('pages.info.terms.articles.art15.p3') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-16" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >16</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 16 –
                    {{ t('pages.info.terms.articles.art16.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>16.1</strong>
                      {{ t('pages.info.terms.articles.art16.p1') }}
                    </p>
                    <p>
                      <strong>16.2</strong>
                      {{ t('pages.info.terms.articles.art16.p4') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-17" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >17</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 17 –
                    {{ t('pages.info.terms.articles.art17.title') }}
                  </h2>
                  <p class="text-lighter leading-relaxed">
                    {{ t('pages.info.terms.articles.art17.desc') }}
                  </p>
                </section>

                <section id="artikel-18" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >18</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 18 –
                    {{ t('pages.info.terms.articles.art18.title') }}
                  </h2>
                  <p class="text-lighter leading-relaxed">
                    {{ t('pages.info.terms.articles.art18.desc') }}
                  </p>
                </section>

                <section id="artikel-19" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >19</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 19 –
                    {{ t('pages.info.terms.articles.art19.title') }}
                  </h2>
                  <p class="text-lighter leading-relaxed">
                    {{ t('pages.info.terms.articles.art19.desc') }}
                    <br /><br />
                    <a
                      href="https://www.bistroo.com/downloads/avg.pdf"
                      target="_blank"
                      class="text-primary font-bold"
                      >> {{ t('pages.info.terms.articles.art19.download_link') }}</a
                    >
                  </p>
                </section>

                <section id="artikel-20" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >20</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 20 –
                    {{ t('pages.info.terms.articles.art20.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>20.1</strong>
                      {{ t('pages.info.terms.articles.art20.p1') }}
                    </p>
                    <p>
                      <strong>20.2</strong>
                      {{ t('pages.info.terms.articles.art20.p2') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-21" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >21</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 21 –
                    {{ t('pages.info.terms.articles.art21.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>21.1</strong>
                      {{ t('pages.info.terms.articles.art21.p1') }}
                    </p>
                    <p>
                      <strong>21.2</strong>
                      {{ t('pages.info.terms.articles.art21.p2') }}
                    </p>
                    <p>
                      <strong>21.3</strong>
                      {{ t('pages.info.terms.articles.art21.p4') }}
                    </p>
                  </div>
                </section>

                <section id="artikel-22" class="mb-20 scroll-mt-24 !gap-y-10">
                  <h2 class="text-2xl font-bold text-dark mb-6 flex items-center justify-start">
                    <span
                      class="bg-primary/10 text-primary min-w-[40px] h-10 rounded-lg flex items-center justify-center mr-4 text-base"
                      >22</span
                    >
                    {{ t('pages.info.terms.articles.label') }} 22 –
                    {{ t('pages.info.terms.articles.art22.title') }}
                  </h2>
                  <div class="space-y-4 text-lighter leading-relaxed">
                    <p>
                      <strong>22.1</strong>
                      {{ t('pages.info.terms.articles.art22.p1') }}
                    </p>
                    <p>
                      <strong>22.2</strong>
                      {{ t('pages.info.terms.articles.art22.p2') }}
                    </p>
                    <p>
                      <strong>22.3</strong>
                      {{ t('pages.info.terms.articles.art22.p3') }}
                    </p>
                    <p>
                      <strong>22.4</strong>
                      {{ t('pages.info.terms.articles.art22.p4') }}
                    </p>
                  </div>
                </section>

                <div class="pt-12 border-t border-[#ECEDF0] mt-12">
                  <p class="text-xs text-lighter font-medium">
                    {{ t('pages.info.terms.copyright') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />
            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>
        <div
          class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30 lg:min-w-[1136px]">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">
              {{ t('main.cta_block.its_free') }}
            </span>
          </h4>
          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>
          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>
              {{ t('main.cta_block.get_started_for_free') }}
            </span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #ecedf0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}

html {
  scroll-behavior: smooth;
}
</style>
